/**
 * .xlsx worksheet reading — shared strings, styles (date detection), cell
 * value resolution, and dense-grid assembly. Pure manual XML scanning (no
 * DOMParser), so it runs identically in the browser, bun, and Workers.
 *
 * Generalised from apps/ledger-web/src/lib/import_xlsx.ts: reads EVERY
 * worksheet by name (not just sheet1), honours the workbook's 1900/1904 date
 * system, and surfaces which cells were raw numbers (for ID-column warnings).
 */

import { SpreadsheetError, type RawSheet, type RawWorkbook } from '../types.js';
import { excelSerialToIsoDate } from '../serial_date.js';
import { openXlsxZip } from './zip_read.js';
import {
  concatTagText,
  firstTagText,
  getAttr,
  scanElements,
} from './xml_scan.js';

/**
 * Built-in OOXML number-format IDs that render a pure calendar date
 * (ECMA-376 §18.8.30). 18–21/45–47 are pure TIME formats and are excluded.
 */
const BUILTIN_DATE_NUM_FMT_IDS = new Set([14, 15, 16, 17, 22]);

/** How a formula cell with no cached `<v>` result is handled. */
export type FormulaWithoutValueMode = 'error' | 'empty';

/**
 * Parse styles.xml into the set of style indexes whose number format is a
 * calendar date. Mirrors the DOMParser version in import_xlsx.ts exactly: a
 * numeric cell carrying one of these style indexes holds an Excel date serial,
 * not a plain number.
 */
export function parseDateStyleIndexes(stylesXml: string): Set<number> {
  const customDateFmtIds = new Set<number>();
  for (const el of scanElements(stylesXml, 'numFmt')) {
    const id = parseInt(getAttr(el.attrs, 'numFmtId') ?? '', 10);
    if (Number.isNaN(id)) continue;
    const code = getAttr(el.attrs, 'formatCode') ?? '';
    // Strip quoted literal text and bracketed locale/colour tags before
    // checking for date-component letters. 'y'/'d' are unambiguous date markers
    // (unlike 'm', which doubles as minutes). Case-insensitive: LibreOffice
    // writes `DD/MM/YYYY` in upper case.
    const stripped = code.replace(/"[^"]*"/g, '').replace(/\[[^\]]*\]/g, '');
    if (/[dy]/i.test(stripped)) customDateFmtIds.add(id);
  }

  const dateStyleIndexes = new Set<number>();
  let cellXfsInner: string | null = null;
  for (const el of scanElements(stylesXml, 'cellXfs')) {
    cellXfsInner = el.inner;
    break;
  }
  if (cellXfsInner === null) return dateStyleIndexes;

  let styleIndex = 0;
  for (const xf of scanElements(cellXfsInner, 'xf')) {
    const numFmtId = parseInt(getAttr(xf.attrs, 'numFmtId') ?? '0', 10);
    if (
      BUILTIN_DATE_NUM_FMT_IDS.has(numFmtId) ||
      customDateFmtIds.has(numFmtId)
    ) {
      dateStyleIndexes.add(styleIndex);
    }
    styleIndex++;
  }
  return dateStyleIndexes;
}

/** Parse sharedStrings.xml into an ordered array indexed by shared-string id. */
export function parseSharedStrings(xml: string): string[] {
  const strings: string[] = [];
  for (const si of scanElements(xml, 'si')) {
    // A shared-string entry is a plain <t> or rich-text runs (<r><t>…</t></r>);
    // concatenate every <t> descendant either way.
    strings.push(concatTagText(si.inner, 't'));
  }
  return strings;
}

/** Convert a cell reference like "AB12" to a 0-based column index. */
function colRefToIndex(ref: string): number {
  const match = /^([A-Za-z]+)\d+$/.exec(ref);
  if (!match) {
    throw new SpreadsheetError(
      'MALFORMED_XML',
      `Malformed cell reference "${ref}" in .xlsx worksheet.`,
    );
  }
  let index = 0;
  for (const ch of match[1].toUpperCase()) {
    index = index * 26 + (ch.charCodeAt(0) - 64);
  }
  return index - 1;
}

interface CellResult {
  value: string;
  /** The source cell was a raw number (not text, not a date). */
  numeric: boolean;
}

/** Resolve one `<c>` cell (attrs + inner XML) to its display string value. */
function cellValue(
  cellAttrs: string,
  inner: string,
  sharedStrings: string[],
  dateStyleIndexes: Set<number>,
  date1904: boolean,
  cellRef: string,
  formulaMode: FormulaWithoutValueMode,
): CellResult {
  const type = getAttr(cellAttrs, 't');
  if (type === 's') {
    const v = firstTagText(inner, 'v') ?? '';
    const idx = parseInt(v, 10);
    return {
      value: Number.isNaN(idx) ? '' : (sharedStrings[idx] ?? ''),
      numeric: false,
    };
  }
  if (type === 'inlineStr') {
    // Inline string value lives in <is><t>…</t></is>.
    let isInner = '';
    for (const is of scanElements(inner, 'is')) {
      isInner = is.inner;
      break;
    }
    return { value: concatTagText(isInner, 't'), numeric: false };
  }

  // Numeric / boolean / error / formula-string: value lives in <v>.
  const raw = firstTagText(inner, 'v');
  if (raw === null) {
    // No cached value. A formula cell with no result is an error; a genuinely
    // empty cell is just empty.
    if (/<f(\s|>|\/)/.test(inner) && formulaMode === 'error') {
      throw new SpreadsheetError(
        'FORMULA_WITHOUT_VALUE',
        `Cell ${cellRef} holds a formula with no saved result. Open the file in a spreadsheet app, let it recalculate, and save again (or paste the values).`,
      );
    }
    return { value: '', numeric: false };
  }

  if (raw !== '' && type !== 'b' && type !== 'e' && type !== 'str') {
    const sAttr = getAttr(cellAttrs, 's');
    const styleIndex = sAttr !== null ? parseInt(sAttr, 10) : NaN;
    if (!Number.isNaN(styleIndex) && dateStyleIndexes.has(styleIndex)) {
      const serial = parseFloat(raw);
      if (!Number.isNaN(serial)) {
        return {
          value: excelSerialToIsoDate(serial, { date1904 }),
          numeric: false,
        };
      }
    }
    // A plain number (General or non-date numeric format).
    return { value: raw, numeric: true };
  }
  return { value: raw, numeric: false };
}

/** Parse one worksheet's XML into a dense grid + the set of numeric cell refs. */
export function parseWorksheet(
  xml: string,
  name: string,
  sharedStrings: string[],
  dateStyleIndexes: Set<number>,
  date1904: boolean,
  formulaMode: FormulaWithoutValueMode,
): RawSheet {
  const sparse = new Map<number, Map<number, string>>();
  const numericCells = new Set<string>();
  let maxRow = -1;
  let maxCol = -1;

  for (const rowEl of scanElements(xml, 'row')) {
    const rAttr = getAttr(rowEl.attrs, 'r');
    const rowIdx = rAttr ? parseInt(rAttr, 10) - 1 : NaN;
    if (Number.isNaN(rowIdx) || rowIdx < 0) continue;
    maxRow = Math.max(maxRow, rowIdx);

    for (const cellEl of scanElements(rowEl.inner, 'c')) {
      const ref = getAttr(cellEl.attrs, 'r');
      if (!ref) continue;
      const colIdx = colRefToIndex(ref);
      maxCol = Math.max(maxCol, colIdx);

      const { value, numeric } = cellValue(
        cellEl.attrs,
        cellEl.inner,
        sharedStrings,
        dateStyleIndexes,
        date1904,
        ref,
        formulaMode,
      );
      let rowMap = sparse.get(rowIdx);
      if (!rowMap) {
        rowMap = new Map<number, string>();
        sparse.set(rowIdx, rowMap);
      }
      rowMap.set(colIdx, value);
      if (numeric) numericCells.add(ref);
    }
  }

  const grid: string[][] = [];
  for (let r = 0; r <= maxRow; r++) {
    const rowMap = sparse.get(r);
    const row: string[] = [];
    for (let c = 0; c <= maxCol; c++) row.push(rowMap?.get(c) ?? '');
    grid.push(row);
  }
  return { name, grid, numericCells };
}

const WORKBOOK_PATH = 'xl/workbook.xml';
const WORKBOOK_RELS_PATH = 'xl/_rels/workbook.xml.rels';
const SHARED_STRINGS_PATH = 'xl/sharedStrings.xml';
const STYLES_PATH = 'xl/styles.xml';

interface SheetRef {
  name: string;
  rId: string;
}

function parseWorkbookSheets(workbookXml: string): {
  sheets: SheetRef[];
  date1904: boolean;
} {
  const sheets: SheetRef[] = [];
  for (const el of scanElements(workbookXml, 'sheet')) {
    const name = getAttr(el.attrs, 'name') ?? '';
    const rId = getAttr(el.attrs, 'r:id') ?? getAttr(el.attrs, 'id') ?? '';
    sheets.push({ name, rId });
  }
  let date1904 = false;
  for (const el of scanElements(workbookXml, 'workbookPr')) {
    const flag = getAttr(el.attrs, 'date1904');
    if (flag === '1' || flag === 'true') date1904 = true;
    break;
  }
  return { sheets, date1904 };
}

function parseRelationships(relsXml: string): Map<string, string> {
  const map = new Map<string, string>();
  for (const el of scanElements(relsXml, 'Relationship')) {
    const id = getAttr(el.attrs, 'Id');
    const target = getAttr(el.attrs, 'Target');
    if (id && target) map.set(id, target);
  }
  return map;
}

/** Resolve a workbook-relative worksheet target to its full ZIP entry path. */
function resolveWorksheetPath(target: string): string {
  if (target.startsWith('/')) return target.slice(1);
  if (target.startsWith('xl/')) return target;
  return `xl/${target}`;
}

export interface ReadXlsxOptions {
  /** Default `'error'`: a formula cell with no cached value throws. */
  formulaWithoutValue?: FormulaWithoutValueMode;
}

/**
 * Read an .xlsx byte array into a {@link RawWorkbook} — every worksheet, by
 * name, in workbook order. Honours the 1900/1904 date system and surfaces
 * which cells were raw numbers.
 */
export async function readXlsx(
  bytes: Uint8Array,
  options: ReadXlsxOptions = {},
): Promise<RawWorkbook> {
  const formulaMode = options.formulaWithoutValue ?? 'error';
  const zip = openXlsxZip(bytes);

  const sharedStrings = zip.has(SHARED_STRINGS_PATH)
    ? parseSharedStrings(await zip.readText(SHARED_STRINGS_PATH))
    : [];
  const dateStyleIndexes = zip.has(STYLES_PATH)
    ? parseDateStyleIndexes(await zip.readText(STYLES_PATH))
    : new Set<number>();

  if (!zip.has(WORKBOOK_PATH)) {
    throw new SpreadsheetError(
      'WORKSHEET_NOT_FOUND',
      'This .xlsx file has no workbook part (xl/workbook.xml).',
    );
  }
  const { sheets: sheetRefs, date1904 } = parseWorkbookSheets(
    await zip.readText(WORKBOOK_PATH),
  );
  const rels = zip.has(WORKBOOK_RELS_PATH)
    ? parseRelationships(await zip.readText(WORKBOOK_RELS_PATH))
    : new Map<string, string>();

  const sheets: RawSheet[] = [];
  let fallbackIndex = 0;
  for (const ref of sheetRefs) {
    const target = rels.get(ref.rId);
    // Fall back to positional worksheetN.xml when a rel is missing/mismatched.
    fallbackIndex++;
    const path = target
      ? resolveWorksheetPath(target)
      : `xl/worksheets/sheet${fallbackIndex}.xml`;
    if (!zip.has(path)) continue;
    const xml = await zip.readText(path);
    sheets.push(
      parseWorksheet(
        xml,
        ref.name || `Sheet${fallbackIndex}`,
        sharedStrings,
        dateStyleIndexes,
        date1904,
        formulaMode,
      ),
    );
  }

  if (sheets.length === 0) {
    throw new SpreadsheetError(
      'WORKSHEET_NOT_FOUND',
      'No worksheets could be read from this .xlsx file.',
    );
  }
  return { sheets };
}

const FIRST_WORKSHEET_PATH = 'xl/worksheets/sheet1.xml';

/**
 * Backwards-compatible single-sheet reader for the legacy ledger-web path:
 * read ONLY `xl/worksheets/sheet1.xml` directly (no workbook.xml lookup, so a
 * bare worksheet-only ZIP still reads), returning its dense grid. A formula
 * cell with no cached value yields `''` here (lenient), preserving the exact
 * prior behaviour of `parseXlsxToCsv`.
 */
export async function readFirstWorksheetGrid(
  bytes: Uint8Array,
): Promise<string[][]> {
  const zip = openXlsxZip(bytes);
  if (!zip.has(FIRST_WORKSHEET_PATH)) {
    throw new SpreadsheetError(
      'WORKSHEET_NOT_FOUND',
      `Could not find a worksheet at "${FIRST_WORKSHEET_PATH}" inside this .xlsx file.`,
    );
  }
  const sharedStrings = zip.has(SHARED_STRINGS_PATH)
    ? parseSharedStrings(await zip.readText(SHARED_STRINGS_PATH))
    : [];
  const dateStyleIndexes = zip.has(STYLES_PATH)
    ? parseDateStyleIndexes(await zip.readText(STYLES_PATH))
    : new Set<number>();
  // No styles.xml means no cell is a date; date system is irrelevant then.
  const sheet = parseWorksheet(
    await zip.readText(FIRST_WORKSHEET_PATH),
    'Sheet1',
    sharedStrings,
    dateStyleIndexes,
    false,
    'empty',
  );
  return sheet.grid;
}
